import React, { useEffect, useState } from 'react';
import type { RootCauseAnalysis, RootCauseCategory } from '../../types/issue';
import { ROOT_CAUSE_CATEGORY_LABELS } from '../../types/issue';
import type { IshikawaHypothesis } from '../../types/issue';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import {
  addHypothesisWhy,
  addIshikawaHypothesis,
  assignDurableHypothesisId,
  closeHypothesisWhys,
  confirmCauseDraft,
  confirmHypothesisCause,
  discardHypothesis,
  durableIshikawa,
  ensureIshikawa,
  ishikawaSummary,
  removeIshikawaHypothesis,
  reviseHypothesis,
  setHypothesisFinding,
  setHypothesisMethod,
  SIX_M,
  toggleIshikawaProbable,
  updateIshikawaHypothesis,
} from '../../utils/causeDraft';
import {
  causeWorkCta,
  consistencySummary,
  EMPTY_CAUSE_CHECK,
  type CauseCheckAnswer,
  type CauseCheckView,
} from '../../utils/causeWork';
import { isCauseConfirmed } from '../../utils/problemCause';
import { IssueEvidenceBoard } from './IssueEvidenceBoard';

export interface CauseObservationContext {
  strata: { dimension: string; highlight: string }[];
  gemba: { fact: string }[];
  evidenceCount: number;
}

const STATUS_LABEL: Record<IshikawaHypothesis['status'], string> = {
  candidate: 'Candidata',
  investigating: 'Em investigação',
  discarded: 'Descartada',
  confirmed: 'Confirmada',
};

const QUESTIONS: { key: keyof Pick<CauseCheckView, 'causeEffect' | 'explainsPhenomenon' | 'technicallyBlockable' | 'unwantedEffects'>; note: keyof Pick<CauseCheckView, 'causeEffectNote' | 'explainsPhenomenonNote' | 'technicallyBlockableNote' | 'unwantedEffectsNote'>; label: string }[] = [
  { key: 'causeEffect', note: 'causeEffectNote', label: 'A causa explica o efeito observado?' },
  { key: 'explainsPhenomenon', note: 'explainsPhenomenonNote', label: 'A causa explica o fenômeno e sua estratificação?' },
  { key: 'technicallyBlockable', note: 'technicallyBlockableNote', label: 'É tecnicamente possível bloquear ou eliminar essa causa?' },
  { key: 'unwantedEffects', note: 'unwantedEffectsNote', label: 'A solução pode gerar efeitos indesejados relevantes?' },
];

const ANSWERS: { value: CauseCheckAnswer; label: string }[] = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'unknown', label: 'Incerto' },
];

function blank(value: RootCauseAnalysis | null): RootCauseAnalysis {
  return {
    problem_statement: value?.problem_statement ?? '',
    root_cause_description: value?.root_cause_description ?? '',
    root_cause_category: value?.root_cause_category ?? 'method',
    five_whys: value?.five_whys ?? [],
    ishikawa: ensureIshikawa(value?.ishikawa),
    suggested_hypotheses: value?.suggested_hypotheses,
    cause_confirmed: value?.cause_confirmed,
    cause_evidence: value?.cause_evidence,
    analysis_method: value?.analysis_method,
  };
}

function consolidationMethod(analysis: RootCauseAnalysis): 'five_whys' | 'ishikawa' | 'simple' | null {
  if (analysis.analysis_method === 'five_whys' || analysis.analysis_method === 'other') return null;
  if (analysis.analysis_method === 'free') return 'simple';
  return 'ishikawa';
}

function isHypothesis(cause: string | IshikawaHypothesis): cause is IshikawaHypothesis {
  return typeof cause !== 'string';
}

function hypothesesOf(analysis: RootCauseAnalysis): IshikawaHypothesis[] {
  return ensureIshikawa(analysis.ishikawa).flatMap((group) => group.causes.filter(isHypothesis));
}

interface CauseAnalysisViewProps {
  value: RootCauseAnalysis | null;
  readOnly?: boolean;
  saving?: boolean;
  onSave: (analysis: RootCauseAnalysis) => void | Promise<void>;
  onReviewObservation?: () => void;
  observation?: CauseObservationContext;
  evidence?: IssueEvidenceRecord[];
  onAddEvidence?: (analysis: RootCauseAnalysis, hypothesisId: string, file: File, caption: string) => Promise<void>;
  onDeleteEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  causeCheck?: CauseCheckView | null;
  onSaveCauseCheck?: (check: CauseCheckView) => Promise<void>;
  focusToken?: string | null;
  onDefinePlan?: () => void;
}

export const CauseAnalysisView: React.FC<CauseAnalysisViewProps> = ({
  value,
  readOnly = false,
  saving = false,
  onSave,
  onReviewObservation,
  observation,
  evidence = [],
  onAddEvidence,
  onDeleteEvidence,
  causeCheck,
  onSaveCauseCheck,
  focusToken,
}) => {
  const [data, setData] = useState<RootCauseAnalysis>(() => blank(value));
  const [openId, setOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [branchDraft, setBranchDraft] = useState<Partial<Record<RootCauseCategory, string>>>({});
  const [whyDraft, setWhyDraft] = useState('');
  const [check, setCheck] = useState<CauseCheckView>(causeCheck ?? EMPTY_CAUSE_CHECK);
  const [error, setError] = useState('');
  const [consolidateError, setConsolidateError] = useState('');
  const [trackedToken, setTrackedToken] = useState<string | null | undefined>(null);

  useEffect(() => {
    setData(blank(value));
  }, [value]);

  useEffect(() => {
    setCheck(causeCheck ?? EMPTY_CAUSE_CHECK);
  }, [causeCheck]);

  if (focusToken && focusToken !== trackedToken && focusToken.includes(':investigate')) {
    setTrackedToken(focusToken);
    const target = hypothesesOf(data).find((item) => item.probable && item.status !== 'discarded' && item.status !== 'confirmed')
      ?? hypothesesOf(data).find((item) => item.status === 'investigating' || item.status === 'candidate');
    if (target) setOpenId(target.id);
  } else if (focusToken !== trackedToken) {
    setTrackedToken(focusToken);
  }

  const branches = ensureIshikawa(data.ishikawa);
  const hypotheses = branches.flatMap((group) => group.causes.filter(isHypothesis));
  const summary = ishikawaSummary(data.ishikawa);
  const probable = hypotheses.filter((item) => item.probable && item.status !== 'discarded');
  const confirmedHypotheses = hypotheses.filter((item) => item.status === 'confirmed');
  const consolidated = isCauseConfirmed({ root_cause_analysis: data });
  const cue = causeWorkCta(data);
  const open = hypotheses.find((item) => item.id === openId) ?? null;
  const whys = (data.five_whys ?? []).filter((entry) => entry.answer.trim());

  useEffect(() => {
    if (!focusToken) return;
    const target = focusToken.includes(':priority')
      ? 'cause-priority'
      : focusToken.includes(':investigate')
        ? 'cause-investigate'
        : focusToken.includes(':prove')
          ? 'cause-prove'
          : focusToken.includes(':consolidate')
            ? 'cause-consolidate'
            : focusToken.includes(':ishikawa')
              ? 'cause-ishikawa'
              : null;
    if (target) document.getElementById(target)?.scrollIntoView({ block: 'nearest' });
  }, [focusToken, openId]);

  const persist = (next: RootCauseAnalysis) => {
    const durable = { ...next, ishikawa: durableIshikawa(next.ishikawa), cause_confirmed: next.cause_confirmed };
    setData(durable);
    void onSave(durable);
  };

  const patchGroups = (groups: RootCauseAnalysis['ishikawa'], nextOpen?: string) => {
    if (nextOpen) setOpenId(nextOpen);
    persist({ ...data, ishikawa: groups, cause_confirmed: data.cause_confirmed });
  };

  const openHypothesis = (category: RootCauseCategory, id: string) => {
    const assigned = assignDurableHypothesisId(data.ishikawa, category, id);
    setData((current) => ({ ...current, ishikawa: assigned.groups, cause_confirmed: current.cause_confirmed }));
    setOpenId(assigned.id);
    setWhyDraft('');
    setError('');
  };

  const chooseMethod = (method: 'five_whys' | 'direct') => {
    if (!open) return;
    patchGroups(setHypothesisMethod(data.ishikawa, open.category, open.id, method), open.id);
  };

  const addWhy = () => {
    if (!open || !whyDraft.trim()) return;
    patchGroups(addHypothesisWhy(data.ishikawa, open.category, open.id, whyDraft), open.id);
    setWhyDraft('');
  };

  const finding = (patch: { conclusion?: string; evidence?: string; discard_reason?: string }) => {
    if (!open) return;
    setData((current) => ({
      ...current,
      ishikawa: setHypothesisFinding(current.ishikawa, open.category, open.id, patch),
      cause_confirmed: current.cause_confirmed,
    }));
  };

  const saveFinding = () => {
    if (!open) return;
    persist({ ...data, cause_confirmed: data.cause_confirmed });
  };

  const confirmHypothesis = () => {
    if (!open) return;
    const groups = confirmHypothesisCause(data.ishikawa, open.category, open.id);
    if (!groups) {
      setError('A hipótese pede conclusão e evidência antes da confirmação.');
      return;
    }
    setError('');
    patchGroups(groups, open.id);
  };

  const discard = () => {
    if (!open) return;
    const groups = discardHypothesis(data.ishikawa, open.category, open.id);
    if (!groups) {
      setError('O descarte pede uma justificativa.');
      return;
    }
    setError('');
    patchGroups(groups, open.id);
  };

  const attach = async (file: File, caption: string) => {
    if (!open || !onAddEvidence) return;
    const assigned = assignDurableHypothesisId(data.ishikawa, open.category, open.id);
    const next = { ...data, ishikawa: durableIshikawa(assigned.groups), cause_confirmed: data.cause_confirmed };
    setData(next);
    setOpenId(assigned.id);
    await onAddEvidence(next, assigned.id, file, caption);
  };

  const applyCheck = (next: CauseCheckView) => {
    setCheck(next);
    if (onSaveCauseCheck) void onSaveCauseCheck(next);
  };

  const steps = ['Fenômeno', 'Ishikawa 6M', 'Causas mais prováveis', 'Investigar hipóteses', 'Comprovar', 'Consolidar causa'];
  const stepIndex = cue.focus === 'plan'
    ? 5
    : cue.focus === 'consolidate'
      ? 5
      : cue.focus === 'prove'
        ? 4
        : cue.focus === 'investigate'
          ? 3
          : cue.focus === 'priority'
            ? 2
            : data.problem_statement.trim()
              ? 1
              : 0;

  return (
    <div className="min-w-0 space-y-3">
      <p className="flex flex-wrap gap-x-1 text-[11px] font-semibold tracking-wide text-slate-400">
        {steps.map((label, index) => (
          <span key={label}>
            <span className={index === stepIndex ? 'text-slate-900 dark:text-slate-100' : ''}>{label}</span>
            {index < steps.length - 1 ? ' → ' : ''}
          </span>
        ))}
      </p>
      <p className="text-[12px] text-slate-500">
        {summary.total} no 6M · {probable.length} mais {probable.length === 1 ? 'provável' : 'prováveis'} · {summary.investigating} em investigação · {summary.confirmed} {summary.confirmed === 1 ? 'confirmada' : 'confirmadas'} · {consolidated ? 'Causa consolidada' : 'Causa em aberto'}
      </p>

      <section className="rounded border border-slate-200 px-2.5 py-2 dark:border-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Observação</p>
            <p className="text-[13px] leading-snug text-slate-800 dark:text-slate-100">
              <span className="font-semibold">Fenômeno. </span>
              {data.problem_statement.trim() || 'Ainda não descrito.'}
            </p>
            {(observation?.strata ?? []).slice(0, 2).map((item) => (
              <p key={item.dimension} className="text-[12px] leading-snug text-slate-600">
                <span className="font-medium text-slate-800">{item.dimension}. </span>{item.highlight || 'Sem fatias.'}
              </p>
            ))}
            {(observation?.gemba ?? []).slice(0, 2).map((item) => (
              <p key={item.fact} className="text-[12px] leading-snug text-slate-600">
                <span className="font-medium text-slate-800">Gemba. </span>{item.fact}
              </p>
            ))}
            <p className="text-[12px] text-slate-500">Evidências da observação: {observation?.evidenceCount ?? 0}</p>
          </div>
          {onReviewObservation ? (
            <button type="button" onClick={onReviewObservation} className="shrink-0 text-[12px] font-medium text-slate-700 hover:underline">Ver observação</button>
          ) : null}
        </div>
      </section>

      {whys.length > 0 ? (
        <details className="rounded border border-slate-200 px-2.5 py-1.5">
          <summary className="cursor-pointer text-[12px] font-medium text-slate-700">Registro anterior — 5 Porquês do problema</summary>
          <ol className="mt-1 space-y-1 text-[12px] text-slate-700">
            {whys.map((entry) => <li key={entry.level}>{entry.answer}</li>)}
          </ol>
        </details>
      ) : null}

      <div className={summary.total > 0 ? 'grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_18rem]' : ''}>
        <section id="cause-ishikawa" className="min-w-0">
          <h3 className="mb-2 text-[13px] font-semibold text-slate-900 dark:text-slate-50">Causas potenciais — Ishikawa 6M</h3>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {SIX_M.map((category) => {
              const group = branches.find((item) => item.category === category);
              const causes = (group?.causes ?? []).filter(isHypothesis);
              return (
                <article key={category} className="min-w-0 rounded border border-slate-200 dark:border-slate-800">
                  <header className="flex items-center justify-between border-b border-slate-100 px-2 py-1.5 dark:border-slate-800">
                    <h4 className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">{ROOT_CAUSE_CATEGORY_LABELS[category]}</h4>
                    <span className="text-[11px] text-slate-400">{causes.length}</span>
                  </header>
                  {causes.length === 0 ? (
                    <p className="px-2 py-2 text-[12px] text-slate-400">Nenhuma hipótese.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                      {causes.map((item) => (
                        <li key={item.id} className="space-y-1 px-2 py-1.5">
                          {editingId === item.id && !readOnly ? (
                            <input
                              value={editText}
                              aria-label={`Editar hipótese ${item.text}`}
                              onChange={(event) => setEditText(event.target.value)}
                              onBlur={() => {
                                if (editText.trim()) patchGroups(updateIshikawaHypothesis(data.ishikawa, category, item.id, editText));
                                setEditingId(null);
                              }}
                              className="w-full rounded border border-slate-300 px-1.5 py-1 text-[12px]"
                            />
                          ) : (
                            <p className="text-[13px] leading-snug text-slate-900 dark:text-slate-50">{item.text}</p>
                          )}
                          <p className="flex flex-wrap gap-1 text-[11px]">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700">{STATUS_LABEL[item.status]}</span>
                            {item.probable && item.status !== 'discarded' ? (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">Mais provável</span>
                            ) : null}
                          </p>
                          {item.status === 'discarded' && item.investigation?.discard_reason ? (
                            <p className="text-[12px] leading-snug text-slate-500">{item.investigation.discard_reason}</p>
                          ) : null}
                          {!readOnly && item.status !== 'discarded' && item.status !== 'confirmed' ? (
                            <p className="flex flex-wrap gap-x-2 text-[12px]">
                              <button type="button" className="font-medium text-slate-700 hover:underline" onClick={() => { setEditingId(item.id); setEditText(item.text); }}>Editar</button>
                              <button type="button" className="font-medium text-slate-700 hover:underline" onClick={() => patchGroups(toggleIshikawaProbable(data.ishikawa, category, item.id))}>Marcar provável</button>
                              <button type="button" className="font-medium text-slate-700 hover:underline" onClick={() => openHypothesis(category, item.id)}>Investigar</button>
                              <button type="button" className="font-medium text-slate-700 hover:underline" onClick={() => openHypothesis(category, item.id)}>Descartar</button>
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!readOnly ? (
                    <form
                      className="flex gap-1 border-t border-slate-100 p-1.5"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const text = branchDraft[category]?.trim() ?? '';
                        if (!text) return;
                        patchGroups(addIshikawaHypothesis(data.ishikawa, category, text));
                        setBranchDraft((current) => ({ ...current, [category]: '' }));
                      }}
                    >
                      <input
                        value={branchDraft[category] ?? ''}
                        aria-label={`Hipótese em ${ROOT_CAUSE_CATEGORY_LABELS[category]}`}
                        onChange={(event) => setBranchDraft((current) => ({ ...current, [category]: event.target.value }))}
                        placeholder="Nova hipótese"
                        className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-1 text-[12px]"
                      />
                      <button type="submit" className="rounded border border-slate-300 px-2 text-[12px] font-medium text-slate-700">Incluir</button>
                    </form>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>

        {summary.total > 0 ? (
          <aside id="cause-priority" className="rounded border border-slate-200 p-2.5 dark:border-slate-800">
            <h3 className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">Causas mais prováveis</h3>
            <p className="mt-1 text-[12px] leading-snug text-slate-500">6M é o levantamento. Mais provável é a priorização. 5 Porquês é o aprofundamento. A causa do problema nasce na consolidação.</p>
            {probable.length === 0 ? (
              <p className="mt-2 text-[12px] text-slate-500">Nenhuma hipótese priorizada.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {probable.map((item) => (
                  <li key={item.id} className="text-[12px] leading-snug">
                    <p className="font-medium text-slate-800">{ROOT_CAUSE_CATEGORY_LABELS[item.category]}</p>
                    <p className="text-slate-700">{item.text}</p>
                    <p className="text-slate-500">{STATUS_LABEL[item.status]}</p>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        ) : null}
      </div>

      {open ? (
        <section id="cause-investigate" className="rounded border border-slate-300 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Hipótese · {ROOT_CAUSE_CATEGORY_LABELS[open.category]}</p>
              <h3 className="text-[15px] font-semibold text-slate-950">{open.text}</h3>
              <p className="text-[12px] text-slate-500">{STATUS_LABEL[open.status]}{open.probable ? ' · Mais provável' : ''}</p>
            </div>
            <button type="button" onClick={() => setOpenId(null)} className="text-[12px] font-medium text-slate-600 hover:underline">Fechar</button>
          </div>

          {open.status !== 'confirmed' && open.status !== 'discarded' && !readOnly ? (
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => chooseMethod('five_whys')} className={`rounded border px-2 py-1 text-[12px] font-medium ${open.investigation?.method === 'five_whys' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}>5 Porquês</button>
              <button type="button" onClick={() => chooseMethod('direct')} className={`rounded border px-2 py-1 text-[12px] font-medium ${open.investigation?.method === 'direct' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}>Análise direta</button>
            </div>
          ) : null}

          {open.investigation?.method === 'five_whys' ? (
            <div className="mt-3 space-y-1">
              <p className="text-[13px] font-medium text-slate-900">{open.text}</p>
              {(open.investigation.whys ?? []).map((why, index) => (
                <div key={why.id}>
                  <p className="pl-3 text-[11px] text-slate-400">↓ porque</p>
                  <p className="text-[12px] font-medium text-slate-500">Por quê {index + 1}</p>
                  <p className="text-[13px] text-slate-800">{why.answer}</p>
                </div>
              ))}
              {!readOnly && open.status !== 'confirmed' && open.status !== 'discarded' && !open.investigation.whys_closed ? (
                <form className="flex gap-1 pt-1" onSubmit={(event) => { event.preventDefault(); addWhy(); }}>
                  <p className="self-center pl-3 text-[11px] text-slate-400">↓ porque</p>
                  <input value={whyDraft} aria-label="Próximo porquê" onChange={(event) => setWhyDraft(event.target.value)} placeholder="Próximo porquê" className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-[12px]" />
                  <button type="submit" className="rounded border border-slate-300 px-2 text-[12px] font-medium text-slate-700">Incluir</button>
                </form>
              ) : null}
              {!readOnly && (open.investigation.whys?.length ?? 0) > 0 && !open.investigation.whys_closed && open.status !== 'confirmed' && open.status !== 'discarded' ? (
                <button type="button" onClick={() => patchGroups(closeHypothesisWhys(data.ishikawa, open.category, open.id), open.id)} className="text-[12px] font-medium text-slate-700 hover:underline">Encerrar quando a profundidade bastar</button>
              ) : null}
              {open.investigation.whys_closed ? <p className="text-[12px] text-slate-500">Cadeia encerrada.</p> : null}
            </div>
          ) : null}

          {open.investigation?.method === 'direct' ? (
            <p className="mt-3 text-[12px] text-slate-500">Análise direta desta hipótese.</p>
          ) : null}

          <div className="mt-3 grid gap-3 xl:grid-cols-2">
            <label className="block">
              <span className="text-[12px] font-medium text-slate-600">Conclusão da hipótese</span>
              {readOnly || open.status === 'confirmed' || open.status === 'discarded' ? (
                <p className="text-[13px] text-slate-800">{open.investigation?.conclusion || 'Sem conclusão.'}</p>
              ) : (
                <textarea value={open.investigation?.conclusion ?? ''} rows={2} onChange={(event) => finding({ conclusion: event.target.value })} onBlur={saveFinding} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]" />
              )}
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-slate-600">Evidência textual</span>
              {readOnly || open.status === 'confirmed' || open.status === 'discarded' ? (
                <p className="text-[13px] text-slate-800">{open.investigation?.evidence || 'Sem evidência.'}</p>
              ) : (
                <textarea value={open.investigation?.evidence ?? ''} rows={2} onChange={(event) => finding({ evidence: event.target.value })} onBlur={saveFinding} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]" />
              )}
            </label>
          </div>

          <div className="mt-3">
            <p className="mb-1 text-[12px] font-medium text-slate-600">Anexos da hipótese</p>
            <IssueEvidenceBoard
              records={evidence.filter((record) => record.hypothesis_id === open.id)}
              editable={!readOnly && Boolean(onAddEvidence)}
              onAdd={onAddEvidence ? attach : undefined}
              onDelete={onDeleteEvidence}
              emptyLabel="Nenhum anexo nesta hipótese."
            />
            <p className="mt-1 text-[12px] text-slate-500">O anexo registra a hipótese. A confirmação continua nos botões abaixo.</p>
          </div>

          {open.status !== 'confirmed' && open.status !== 'discarded' && !readOnly ? (
            <div className="mt-3 grid gap-3 border-t border-slate-200 pt-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="block">
                  <span className="text-[12px] font-medium text-slate-600">Justificativa do descarte</span>
                  <textarea value={open.investigation?.discard_reason ?? ''} rows={2} onChange={(event) => finding({ discard_reason: event.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]" />
                </label>
                <button type="button" onClick={discard} className="rounded border border-slate-300 px-3 py-1.5 text-[12px] font-medium text-slate-700">Descartar hipótese</button>
              </div>
              <div className="flex items-end">
                <button type="button" onClick={confirmHypothesis} className="rounded bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white">Confirmar hipótese como causa</button>
              </div>
            </div>
          ) : null}
          {open.status === 'discarded' ? (
            <p className="mt-2 text-[12px] text-slate-600">Descarte: {open.investigation?.discard_reason || 'Sem justificativa.'}</p>
          ) : null}
          {error ? <p className="mt-2 text-[12px] text-red-700">{error}</p> : null}
          {!readOnly && open.status === 'candidate' ? (
            <button type="button" onClick={() => patchGroups(removeIshikawaHypothesis(data.ishikawa, open.category, open.id))} className="mt-2 text-[12px] text-slate-500 hover:underline">Remover hipótese</button>
          ) : null}
        </section>
      ) : null}

      <div className="grid items-start gap-3 xl:grid-cols-2">
        <section id="cause-prove" className="rounded border border-slate-200 p-3 dark:border-slate-800">
          <h3 className="text-[13px] font-semibold text-slate-900">Teste de consistência</h3>
          <p className="mt-1 text-[13px] font-medium text-slate-800">{consistencySummary(check)}</p>
          <p className="text-[12px] text-slate-500">A consolidação continua sendo uma decisão humana.</p>
          <ul className="mt-2 space-y-2">
            {QUESTIONS.map((question) => (
              <li key={question.key}>
                <p className="text-[12px] text-slate-800">{question.label}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {ANSWERS.map((answer) => (
                    <button
                      key={answer.value}
                      type="button"
                      aria-pressed={check[question.key] === answer.value}
                      disabled={readOnly || !onSaveCauseCheck}
                      onClick={() => applyCheck({ ...check, [question.key]: answer.value })}
                      className={`rounded border px-2 py-0.5 text-[12px] ${check[question.key] === answer.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'} disabled:opacity-70`}
                    >
                      {answer.label}
                    </button>
                  ))}
                </div>
                {readOnly ? (
                  check[question.note] ? <p className="mt-1 text-[12px] text-slate-500">{check[question.note]}</p> : null
                ) : (
                  <input
                    value={check[question.note]}
                    aria-label={`Nota: ${question.label}`}
                    disabled={!onSaveCauseCheck}
                    onChange={(event) => setCheck((current) => ({ ...current, [question.note]: event.target.value }))}
                    onBlur={(event) => {
                      if (onSaveCauseCheck) void onSaveCauseCheck({ ...check, [question.note]: event.target.value });
                    }}
                    placeholder="Nota"
                    className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-[12px]"
                  />
                )}
              </li>
            ))}
          </ul>
        </section>

        <section id="cause-consolidate" className="rounded border border-slate-200 p-3 dark:border-slate-800">
          <h3 className="text-[13px] font-semibold text-slate-900">Consolidar causa</h3>
          {data.root_cause_description.trim() && !consolidated ? (
            <p className="mt-1 text-[12px] font-medium text-amber-800">Registro anterior, sem confirmação</p>
          ) : null}
          {consolidated ? <p className="mt-1 text-[12px] font-medium text-slate-700">Causa consolidada do problema</p> : null}
          <p className="mt-1 text-[12px] text-slate-500">Resultado do teste: {consistencySummary(check)}</p>
          <div className="mt-2">
            <p className="text-[12px] font-medium text-slate-700">Hipóteses confirmadas</p>
            {confirmedHypotheses.length === 0 ? (
              <p className="text-[12px] text-slate-500">Nenhuma hipótese confirmada.</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {confirmedHypotheses.map((item) => (
                  <li key={item.id} className="text-[12px] leading-snug text-slate-700">
                    <span className="font-medium text-slate-900">{ROOT_CAUSE_CATEGORY_LABELS[item.category]}. </span>
                    {item.text}
                    {item.investigation?.evidence ? ` · Evidência: ${item.investigation.evidence}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="mt-2 text-[12px] text-slate-500">Escreva a causa do problema com as hipóteses que a sustentam.</p>
          <label className="mt-2 block">
            <span className="text-[12px] font-medium text-slate-600">Causa do problema</span>
            {readOnly ? (
              <p className="text-[13px] text-slate-800">{data.root_cause_description.trim() || 'Sem causa consolidada.'}</p>
            ) : (
              <textarea
                value={data.root_cause_description}
                rows={3}
                onChange={(event) => setData(reviseHypothesis(data, event.target.value))}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]"
              />
            )}
          </label>
          <label className="mt-2 block">
            <span className="text-[12px] font-medium text-slate-600">Evidência da causa</span>
            {readOnly ? (
              <p className="text-[13px] text-slate-800">{data.cause_evidence?.trim() || 'Sem evidência.'}</p>
            ) : (
              <textarea
                value={data.cause_evidence ?? ''}
                rows={2}
                onChange={(event) => setData((current) => ({ ...current, cause_evidence: event.target.value, cause_confirmed: false }))}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]"
              />
            )}
          </label>
          {!readOnly ? (
            <label className="mt-2 block text-[12px] text-slate-600">
              Categoria
              <select
                value={data.root_cause_category}
                onChange={(event) => setData((current) => ({ ...current, root_cause_category: event.target.value as RootCauseCategory }))}
                className="mt-1 block rounded border border-slate-300 px-2 py-1 text-[12px]"
              >
                {SIX_M.map((category) => (
                  <option key={category} value={category}>{ROOT_CAUSE_CATEGORY_LABELS[category]}</option>
                ))}
              </select>
            </label>
          ) : null}
          {!readOnly ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  const next = confirmCauseDraft(data, consolidationMethod(data));
                  if (!next) {
                    setConsolidateError('A causa do problema pede descrição e evidência.');
                    return;
                  }
                  setConsolidateError('');
                  persist(next);
                }}
                className="rounded bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-40"
              >
                Confirmar causa raiz do problema
              </button>
              <button type="button" disabled={saving} onClick={() => persist(data)} className="rounded border border-slate-300 px-3 py-1.5 text-[12px] font-medium text-slate-700">Salvar análise</button>
            </div>
          ) : null}
          {consolidateError ? <p className="mt-2 text-[12px] text-red-700">{consolidateError}</p> : null}
        </section>
      </div>
    </div>
  );
};
